import React, { useState, useMemo, useCallback } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import { Layout, Breadcrumb, Avatar, Image } from 'antd';
import { MenuItem, makeStyles } from '@material-ui/core';
import { useGetCustomerProfile } from 'src/reactQuery/customers/queries/useGetCustomerProfile';
import { useGetOrder } from 'src/reactQuery/orders/useGetOrder';
import { useUpdateOrder } from 'src/reactQuery/orders/useUpdateOrder';
import { getDataFromLocalStorage } from 'src/localStorage/getDataFromLocalStorage';
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum';
import { isCurrentProjectBuildy } from 'src/util/projectCheckers';
import ArrowNextIcon from '../../../asset/arrow-next.svg';
import CButton from '../../../component/Button';
import Loader from '../../../component/Loader/Loader';
import NoxTable from '../../../component/Table';
import InnerHeader from '../../../component/InnerHeader';
import formatDate from '../../../helpers/formatDate';
import { DefaultSelect } from '../../../component/Select';

const useStyles = makeStyles({
  gridContainer: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '33% 33% 33%',
    marginLeft: '90px',
    marginTop: '55px'
  },
  gridHeader: {
    gridColumn: '33% 33% 33%',
    gridRow: '1',
    textAlign: 'start'
  },
  gridItem: {
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridItemPayment: {
    display: 'flex',
    flexDirection: 'column',
    gridColumn: '3',
    gridRow: '1 / span 2',
    height: '100%',
    justifyContent: 'space-between',
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridItemSelect: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '90%',
    textAlign: 'start'
  },
  p: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%'
  },
  selectHeader: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    margin: '0px'
  },
  span: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    overflowWrap: 'break-word'
  },
  usersInfo: {
    display: 'flex'
  }
});

const OrderDetail = () => {
  const history = useHistory();
  const { orderId } = useParams();
  const classes = useStyles();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { data: orderData, isLoading, refetch: orderRefetch } = useGetOrder(orderId);
  const { data: profileData } = useGetCustomerProfile(orderData?.sellerProfile?.userId);
  const { mutate } = useUpdateOrder({
    onSuccess: () => {
      orderRefetch();
    }
  });
  const [selectedStatus, setSelectedStatus] = useState();
  const [selectedAddress, setSelectedAddress] = useState();

  const statuses = [
    'accepted',
    'processing',
    'onHold',
    'cancelled',
    'delivering',
    'delivered',
    'completed',
    'refunded',
    'failed',
    'trash',
    'draft'
  ];
  const currentProject = getDataFromLocalStorage(LocalStorageKey.CURRENT_PROJECT);
  const isCurrentPRojectStimull = currentProject?._id === '6475d53f36eeee8e29db732b';

  const selectedOrder = useMemo(() => {
    if (!orderData) return;

    return orderData;
  }, [orderData]);

  const sellerAddresses = useMemo(() => {
    if (!profileData) return;

    if (isCurrentPRojectStimull) {
      return [];
    }

    return profileData.settings.addresses;
  }, [profileData, orderData, orderId]);

  function itemRender(route, params, routes) {
    const last = routes.indexOf(route) === routes.length - 1;

    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  }

  const routes = [
    {
      breadcrumbName: 'Orders',
      path: '/ecommerce/orders'
    },
    {
      breadcrumbName: selectedOrder?._id,
      path: `/ecommerce/orders/${orderId}`
    }
  ];

  const onUpdate = (status) => {
    mutate({
      body: {
        pickupAddress: selectedAddress || orderData.pickupAddress,
        status: typeof status === 'string' ? status : selectedStatus
      },
      orderId
    });
    history.push('/ecommerce/orders');
  };

  const mapToTable = (items) => {
    const data = [];

    if (!items || !items.length) {
      return data;
    }

    items.forEach(({ itemId, selectedOption, price, quantity, item }, index) => {
      data.push({
        color: selectedOption?.color ?? '',
        id: itemId,
        key: index,
        link: item?.imageUrl ?? '',
        price: price ?? `${0} $`,
        qty: quantity,
        size: selectedOption?.size?.unit ?? '',
        title: item?.title ?? '',
        weight: item?.weight ?? ''
      });
    });

    return data;
  };

  const data = mapToTable(selectedOrder?.selectedItems);

  const columns = [
    {
      dataIndex: 'link',
      key: 'link',
      render: (link) => (
        <Image height={40} src={link} style={{ objectFit: 'contain' }} width={40} />
      ),
      title: () => {
        return <div style={{ textAlign: 'center' }}>Preview</div>;
      }
    },
    {
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
      title: 'Title'
    },

    {
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => a.price.localeCompare(b.price),
      title: 'Price'
    },
    {
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id.localeCompare(b.id),
      title: 'Item ID'
    },
    {
      dataIndex: 'qty',
      key: 'qty',
      sorter: (a, b) => a.qty.localeCompare(b.qty),
      title: 'Quantity'
    },
    {
      dataIndex: 'color',
      key: 'color',
      sorter: (a, b) => a.color.localeCompare(b.color),
      title: 'Color'
    },
    {
      dataIndex: 'size',
      key: 'size',
      sorter: (a, b) => a.size.localeCompare(b.size),
      title: 'Size'
    },
    {
      dataIndex: 'weight',
      key: 'weight',
      sorter: (a, b) => a.weight.localeCompare(b.weight),
      title: 'Weight'
    }
  ];

  const OrderHeader = useCallback(() => {
    if (!selectedOrder) return <Loader />;
    const { _id, title, createdAt, selectedItems } = selectedOrder;
    const finalDate = formatDate(createdAt);

    return (
      <div
        style={{
          display: 'flex'
        }}
      >
        <Avatar
          src={selectedItems[0]?.item?.imageUrl ?? '-'}
          style={{
            display: 'flex',
            height: '55px',
            width: '55px'
          }}
        />
        <div
          className={classes.gridContainer}
          style={{
            marginLeft: '35px',
            marginTop: '0px',
            width: '100%'
          }}
        >
          <div className={classes.gridHeader}>
            <span className={classes.span}>{title || '-'}</span>
            <p className={classes.p}>
              ID: <span style={{ color: 'black' }}>{_id || '-'}</span>
            </p>
          </div>
          <div className={classes.gridHeader}>
            <span className={classes.span}>{finalDate || '-'}</span>
            <p className={classes.p}>Date & Time</p>
          </div>
          <div className={classes.gridItemSelect}>
            <p className={classes.selectHeader}>Status:</p>
            <DefaultSelect
              defaultValue={orderData.status}
              onSelect={(value) => setSelectedStatus(value)}
              renderValue={(status) => status}
              selectStyles={{ marginLeft: '8px' }}
              value={selectedStatus}
            >
              <MenuItem key="asdas" value="status">
                status
              </MenuItem>
              {statuses.map((status) => {
                return (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                );
              })}
            </DefaultSelect>
          </div>
        </div>
      </div>
    );
  }, [selectedOrder]);

  const OrderBody = useCallback(() => {
    if (!selectedOrder || !sellerAddresses) return <Loader />;
    const {
      pickupAddress,
      dropoffAddress,
      totalPrice,
      sellerProfile,
      customerProfile,
      deliveryFee
    } = selectedOrder;
    const sellerAddress = `${pickupAddress?.name || '-'}, ${pickupAddress?.street || '-'}, ${
      pickupAddress?.apartment || '-'
    }, ${pickupAddress?.phone_number?.country_code || '-'} ${
      pickupAddress?.phone_number?.number || '-'
    }`;
    const buyerAddress = `${dropoffAddress?.name || '-'}, ${dropoffAddress?.city || '-'}, ${
      dropoffAddress?.street || '-'
    }, ${dropoffAddress?.apartment || '-'}, ${dropoffAddress?.phone_number?.country_code || '-'} ${
      dropoffAddress?.phone_number?.number || '-'
    }`;

    const renderAdditionalComment = (additionalComment) => {
      if (additionalComment?.length) {
        return `(${additionalComment})`;
      }
      return '';
    };

    const renderFullAddress = (address) => {
      if (address) {
        return `${address?.name}, ${address?.street}, ${
          address?.apartment
        }, ${renderAdditionalComment(address?.additional_comment)}`;
      }
      return '-';
    };

    return (
      <div className={classes.gridContainer}>
        <div className={classes.gridItem}>
          <p className={classes.p}>Seller</p>
          <div className={classes.usersInfo}>
            <Avatar
              src={sellerProfile?.profilePictureUrl}
              style={{
                display: 'flex',
                height: '55px',
                marginRight: '8px',
                width: '55px'
              }}
            />
            <div className={classes.gridItem}>
              <span className={classes.span}>{sellerProfile?.userName || '-'}</span>
              <p className={classes.p}>
                ID: <span style={{ color: 'black' }}>{sellerProfile?.userId || '-'}</span>
              </p>
            </div>
          </div>
        </div>

        <div className={classes.gridItem}>
          <p className={classes.p}>Seller Address</p>

          {selectedStatus !== 'delivering' && isCurrentProjectBuildy ? (
            <div>
              <DefaultSelect
                defaultValue={sellerAddresses[0]}
                onSelect={(value) => {
                  setSelectedAddress(value);
                }}
                renderValue={renderFullAddress}
                selectStyles={{ marginLeft: '8px' }}
                value={selectedAddress}
              >
                {sellerAddresses
                  .filter((rawAddress) => rawAddress !== null)
                  .map((address) => {
                    return (
                      <MenuItem key={address._id} value={address}>
                        {renderFullAddress(address)}
                      </MenuItem>
                    );
                  })}
              </DefaultSelect>
            </div>
          ) : (
            <span className={classes.span}>
              {sellerAddress || '-'} {renderAdditionalComment(pickupAddress?.additional_comment)}
            </span>
          )}
        </div>

        <div className={classes.gridItemPayment}>
          <div className={classes.gridItem}>
            <p className={classes.p}>Payment Method</p>
            <span className={classes.span}>-</span>
          </div>
          <div className={classes.gridItem}>
            <p className={classes.p}>Total Price</p>
            <span className={classes.span}>
              {(selectedOrder?.groupId ? totalPrice : totalPrice + deliveryFee).toFixed(2) || '0'}
            </span>
          </div>
          {!selectedOrder?.groupId && (
            <div className={classes.gridItem}>
              <p className={classes.p}>Delivery Price</p>
              <span className={classes.span}>{deliveryFee || '0'}</span>
            </div>
          )}
        </div>

        <div className={classes.gridItem}>
          <p className={classes.p}>Buyer</p>
          <div className={classes.usersInfo}>
            <Avatar
              src={customerProfile?.profilePictureUrl}
              style={{
                display: 'flex',
                height: '55px',
                marginRight: '8px',
                width: '55px'
              }}
            />
            <div className={classes.gridItem}>
              <span className={classes.span}>{customerProfile?.userName || '-'}</span>
              <p className={classes.p}>
                ID: <span style={{ color: 'black' }}>{customerProfile?.userId || '-'}</span>
              </p>
            </div>
          </div>
        </div>

        <div className={classes.gridItem}>
          <p className={classes.p}>Buyer Address</p>
          <span className={classes.span}>
            {buyerAddress || '-'} {renderAdditionalComment(dropoffAddress?.additional_comment)}
          </span>
        </div>
      </div>
    );
  }, [selectedOrder, sellerAddresses]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Layout style={{ background: 'white' }}>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '35px 40px 0px 35px'
        }}
      >
        <Breadcrumb
          itemRender={itemRender}
          routes={routes}
          separator={<img alt="separator icon" src={ArrowNextIcon} />}
          style={{
            background: 'white',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        />
        <div>
          <CButton onClick={onUpdate} type="primary">
            Save
          </CButton>

          <CButton onClick={() => onUpdate('cancelled')} type="primary">
            Cancel Order
          </CButton>
        </div>
      </div>
      <div style={{ marginTop: '55px', padding: '0px 40px 0px 35px' }}>
        {OrderHeader()}
        {OrderBody()}
      </div>
      <div
        style={{
          height: 'calc(100vh - 165px)',
          marginTop: '80px',
          overflowY: 'auto'
        }}
      >
        <InnerHeader subtitle={selectedOrder?.selectedItems?.length} title="Items" />

        <NoxTable
          checkbox={false}
          columns={isCurrentPRojectStimull ? columns.slice(0, 5) : columns}
          data={data}
          searchBy="title"
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      </div>
    </Layout>
  );
};

export default OrderDetail;
